import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { Crest, FlagStrip } from "@/components/Brand";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import Captcha from "@/components/Captcha";
import ForgotPasswordDialog from "@/components/ForgotPasswordDialog";
import PasswordInput from "@/components/PasswordInput";
import { api, errMsg } from "@/lib/api";
import { useAuth, homeFor, userRoles } from "@/context/AuthContext";

const F = ({ label, ...p }) => (
  <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">{label}</Label>
    {p.type === "password"
      ? <PasswordInput className="h-11" testid={p["data-testid"]} {...p} />
      : <Input className="h-11" {...p} />}
  </div>
);

const StationPhoto = () => (
  <div className="rise relative order-2 lg:order-none" data-testid="auth-station-photo">
    <img src="/station.jpg" alt="Passengers boarding intercity coaches at a GPRTU bus terminal in Accra" className="h-44 w-full rounded-[28px] object-cover shadow-[0_30px_60px_-30px_rgba(24,38,120,0.55)] sm:h-56 lg:h-[460px]" />
    <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur lg:bottom-6 lg:left-6 lg:right-auto">
      <Crest className="h-10 w-10" />
      <div className="leading-tight">
        <div className="label-xs">GPRTU union stations</div>
        <div className="font-display text-base font-extrabold text-[#182678]">One ticket system for every terminal</div>
      </div>
    </div>
  </div>
);

export default function AuthPage() {
  const { signIn } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const [login, setLogin] = useState({ identifier: "", password: "" });
  const [reg, setReg] = useState({ name: "", email: "", phone: "", password: "" });
  const [loginOk, setLoginOk] = useState(false);
  const [regOk, setRegOk] = useState(false);
  const [forgot, setForgot] = useState(false);

  const submit = async (path, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(path, body);
      if (path.includes("/login") && !userRoles(data.user).includes("customer")) {
        toast.error("This is a GPRTU staff account. Please use the staff portal.");
        nav("/admin");
        return;
      }
      signIn(data.token, data.user);
      toast.success(`Welcome, ${data.user.name.split(" ")[0]}`);
      nav(location.state?.next || homeFor(data.user));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const btn = (label, testid, disabled) => (
    <Button type="submit" disabled={busy || disabled} className="h-12 w-full rounded-xl bg-[#0052FF] text-base font-bold hover:bg-[#0041CC] btn-press disabled:opacity-60" data-testid={testid}>
      {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : label}
    </Button>
  );

  return (
    <div className="hero-bg relative min-h-full overflow-hidden">
      <Crest variant="navy" className="pointer-events-none absolute -right-32 top-6 h-[520px] w-[520px] opacity-[0.04]" />
      <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-4 py-10 lg:grid-cols-[1.1fr_1fr]">
        <StationPhoto />
        <div className="mx-auto w-full max-w-md">
        <div className="mb-6 flex items-center gap-4" data-testid="auth-brand">
          <span className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-white shadow-[0_12px_28px_-14px_rgba(24,38,120,0.7)] ring-1 ring-slate-200"><Crest className="h-14 w-14" testid="auth-gprtu-crest" /></span>
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#182678]" data-testid="auth-portal-title">Passenger Login</h1>
            <p className="text-sm text-slate-500">Sign in or create an account to book &amp; manage your trips</p>
          </div>
        </div>
        <div className="card-soft overflow-hidden">
          <FlagStrip />
          <div className="p-6">
          <Tabs defaultValue="login">
            <TabsList className="mb-5 grid w-full grid-cols-2 rounded-xl">
              <TabsTrigger value="login" className="rounded-lg" data-testid="auth-login-tab">Login</TabsTrigger>
              <TabsTrigger value="register" className="rounded-lg" data-testid="auth-register-tab">Create account</TabsTrigger>
            </TabsList>
            <TabsContent value="login">
              <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); if (!loginOk) return toast.error("Please solve the code challenge first"); submit("/auth/login", { ...login, scope: "customer" }); }}>
                <F label="Email or phone" value={login.identifier} onChange={(e) => setLogin({ ...login, identifier: e.target.value })} data-testid="login-identifier-input" />
                <F label="Password" type="password" value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} data-testid="login-password-input" />
                <Captcha onValid={setLoginOk} testid="login-captcha" />
                {btn("Login", "login-submit-button", !loginOk)}
                <button type="button" onClick={() => setForgot(true)} className="w-full text-center text-sm font-semibold text-[#0052FF] hover:underline" data-testid="forgot-password-link">Forgot password?</button>
              </form>
            </TabsContent>
            <TabsContent value="register">
              <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); if (!regOk) return toast.error("Please solve the code challenge first"); submit("/auth/register", reg); }}>
                <F label="Full name" value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} data-testid="register-name-input" />
                <F label="Phone number" inputMode="tel" placeholder="024 123 4567" value={reg.phone} onChange={(e) => setReg({ ...reg, phone: e.target.value })} data-testid="register-phone-input" />
                <F label="Email" type="email" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} data-testid="register-email-input" />
                <F label="Password (min 6 characters)" type="password" value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} data-testid="register-password-input" />
                <Captcha onValid={setRegOk} testid="register-captcha" />
                {btn("Create account & earn miles", "register-submit-button", !regOk)}
              </form>
            </TabsContent>
          </Tabs>
          </div>
        </div>
        <div className="mt-4 flex flex-col items-center gap-1.5">
          <p className="flex items-center justify-center gap-1.5 text-xs text-slate-500" data-testid="auth-secured-by"><Lock className="h-3.5 w-3.5" />Secure GPRTU sign-in · Developed by Nacasky Co. Ltd</p>
          <button onClick={() => nav("/admin")} className="text-xs font-semibold text-slate-400 hover:text-[#0052FF]" data-testid="auth-staff-link">GPRTU staff? Go to the staff portal →</button>
        </div>
        </div>
      </div>
      <ForgotPasswordDialog open={forgot} onOpenChange={setForgot} scope="customer" note="Passenger accounts · SMS sent to the number on file" />
    </div>
  );
}
